import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts'
import { formatNumberWithSpaces } from 'utils/numbers'
import { usePrefersReducedMotion } from '../hooks'

// One colour per book everywhere on the page, from the theme variables so dark mode follows.
export const BOOK_COLORS = { 'A-book': 'var(--primary)', 'B-book': 'var(--bb-book-b)' }

const DAY_MS = 24 * 60 * 60 * 1000
const FIELDS = ['a_volume', 'b_volume', 'a_fees', 'b_fees']
export const euro = (value) => `${formatNumberWithSpaces(Math.round(Number(value) || 0))} €`
// one decimal near the ends, so a thin slice reads (0.3 %) and its complement is not rounded up to 100 %
const percent = (share) => `${share < 10 || share > 90 ? share.toFixed(1) : Math.round(share)} %`
export const compact = new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 }) // axis ticks: 220 M, not a wrapped 220 000 000

const monday = (day) => {
  const date = new Date(`${day}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7)
  return date.toISOString().slice(0, 10)
}

// Daily bars turn hair-thin over a long range: group into weeks, then months, like Grafana's auto interval.
export function bucketize(daily, range, fields = FIELDS) {
  const span = (new Date(range.end) - new Date(range.start)) / DAY_MS
  const unit = span > 731 ? 'month' : span > 100 ? 'week' : 'day' // a full quarter stays daily, two full years stay weekly
  if (unit === 'day') return { rows: daily, unit }

  const buckets = new Map()
  for (const row of daily) {
    const key = unit === 'month' ? `${row.day.slice(0, 7)}-01` : monday(row.day)
    const bucket = buckets.get(key) || buckets.set(key, { day: key, ...Object.fromEntries(fields.map((field) => [field, 0])) }).get(key)
    for (const field of fields) bucket[field] += row[field]
  }
  return { rows: [...buckets.values()].sort((a, b) => a.day.localeCompare(b.day)), unit }
}

// YYYY-MM-DD → DD/MM for days and weeks, MM/YYYY for months
export const tickLabel = (unit) => (day) => (unit === 'month' ? `${day.slice(5, 7)}/${day.slice(0, 4)}` : `${day.slice(8, 10)}/${day.slice(5, 7)}`)

// Screen readers get the chart's point as one sentence; the visible data stays in the legend, tooltips and tables.
export const ChartFigure = ({ summary, children }) => <figure className='b_book_chart' role='img' aria-label={summary}>{children}</figure>

// Donut of the opened volume, with each book's share in a legend underneath rather than labels that overflow.
export const BookShareChart = ({ books, emptyLabel, summary }) => {
  const still = usePrefersReducedMotion()
  const data = Object.entries(books).map(([book, { volume }]) => ({ book, volume })).filter(({ volume }) => volume > 0)
  const total = data.reduce((sum, { volume }) => sum + volume, 0)
  if (!total) return <p className='b_book_report_empty'>{emptyLabel}</p>

  return (
    <ChartFigure summary={summary}>
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie isAnimationActive={!still} data={data} dataKey="volume" nameKey="book" innerRadius="55%" outerRadius="90%" paddingAngle={data.length > 1 ? 2 : 0}>
            {data.map(({ book }) => <Cell key={book} fill={BOOK_COLORS[book]} />)}
          </Pie>
          <Tooltip formatter={(value, book) => [`${euro(value)} (${percent(100 * value / total)})`, book]} />
        </PieChart>
      </ResponsiveContainer>
      <ul className='b_book_report_share_legend'>
        {data.map(({ book, volume }) => (
          <li key={book}>
            <span className='b_book_report_swatch' style={{ backgroundColor: BOOK_COLORS[book] }} />
            <span>{book}</span>
            <strong>{percent(100 * volume / total)}</strong>
            <span className='b_book_report_share_amount'>{euro(volume)}</span>
          </li>
        ))}
      </ul>
    </ChartFigure>
  )
}

// Stacked bars per day, week or month, A-book and B-book in the same colours as the donut.
export const BookDailyChart = ({ data, unit, aKey, bKey, emptyLabel, periodLabels, summary }) => {
  const still = usePrefersReducedMotion()
  if (!data?.some((row) => row[aKey] || row[bKey])) return <p className='b_book_report_empty'>{emptyLabel}</p>
  const tick = tickLabel(unit)

  return (
    <ChartFigure summary={summary}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" strokeOpacity={0.6} />
          <XAxis dataKey="day" tickFormatter={tick} tickLine={false} minTickGap={16} />
          <YAxis tickFormatter={(value) => compact.format(value)} tickLine={false} width={60} />
          <Tooltip formatter={(value) => euro(value)} labelFormatter={(day) => `${periodLabels[unit]} ${tick(day)}`} />
          <Legend />
          <Bar isAnimationActive={!still} dataKey={aKey} name="A-book" stackId="books" fill={BOOK_COLORS['A-book']} />
          <Bar isAnimationActive={!still} dataKey={bKey} name="B-book" stackId="books" fill={BOOK_COLORS['B-book']} />
        </BarChart>
      </ResponsiveContainer>
    </ChartFigure>
  )
}